import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatMoney } from '@helpers/index';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';

import { CheckCircleIcon, PriceIcon, WarningIcon } from '../icons';

export interface HealthChangesSummaryProps {
  /**
   * The German names, which decide whether anything changed. The matching
   * `*Labels` are what is shown — passed in rather than looked up here so this
   * stays presentational.
   */
  oldAllergies: string[];
  newAllergies: string[];
  oldHealthIssues: string[];
  newHealthIssues: string[];
  oldAllergyLabels: string[];
  newAllergyLabels: string[];
  oldHealthIssueLabels: string[];
  newHealthIssueLabels: string[];
  oldPrice: number;
  newPrice: number;
  currencyCode: string;
  discount: any;
}

export const HealthChangesSummary: React.FC<HealthChangesSummaryProps> = ({
  oldAllergies,
  newAllergies,
  oldHealthIssues,
  newHealthIssues,
  oldAllergyLabels,
  newAllergyLabels,
  oldHealthIssueLabels,
  newHealthIssueLabels,
  oldPrice,
  newPrice,
  currencyCode,
  discount,
}) => {
  const intl = useIntl();
  const list = (labels: string[]) =>
    labels.length > 0 ? labels.join(', ') : intl.formatMessage({ id: 'GLOBAL.NONE' });

  const allergiesChanged =
    oldAllergies.length !== newAllergies.length ||
    !oldAllergies.every((a) => newAllergies.includes(a));
  const healthIssuesChanged =
    oldHealthIssues.length !== newHealthIssues.length ||
    !oldHealthIssues.every((h) => newHealthIssues.includes(h));
  const priceChanged = Math.abs(newPrice - oldPrice) > 0.01;

  if (!allergiesChanged && !healthIssuesChanged && !priceChanged) return null;

  return (
    <div className="product-preview-step__section">
      <label className="product-preview-step__label">
        <CheckCircleIcon />
        <FormattedMessage id="PET_PROFILE.YOUR_CHANGES" />
      </label>
      <div className="ppu-changes-list">
        {allergiesChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <WarningIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.ALLERGIES" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">{list(oldAllergyLabels)}</span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">{list(newAllergyLabels)}</span>
              </div>
            </div>
          </div>
        )}

        {healthIssuesChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <WarningIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.HEALTH_ISSUES" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">{list(oldHealthIssueLabels)}</span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">{list(newHealthIssueLabels)}</span>
              </div>
            </div>
          </div>
        )}

        {priceChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <PriceIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="GLOBAL.PRICE" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">
                  {formatMoney(oldPrice, currencyCode, intl.locale)}
                </span>
                <span className="ppu-change-item__arrow">→</span>
                <span
                  className={`ppu-change-item__new ${
                    newPrice > oldPrice
                      ? 'ppu-change-item__new--increase'
                      : 'ppu-change-item__new--decrease'
                  }`}>
                  {formatMoney(newPrice, currencyCode, intl.locale)}
                </span>
              </div>
              {discount && (
                <span className="ppu-change-item__discount">
                  <FormattedMessage
                    id="DISCOUNT.APPLIED"
                    values={{
                      label:
                        formatDiscountTitle(intl, discount.title) ||
                        intl.formatMessage({ id: 'DISCOUNT.FALLBACK' }),
                    }}
                  />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
